import React from 'react';
import {
  FileDown,
  Mail,
  Menu,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeft,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { RiqueConversationSidebar } from '@/components/Rique/RiqueConversationSidebar';
import { RiqueMessageList } from '@/components/Rique/RiqueMessageList';
import { RiqueComposer } from '@/components/Rique/RiqueComposer';
import type { RiqueAlert, RiqueBriefing, RiqueConversation, RiqueFaqItem, RiqueMessage } from '@/lib/riqueApi';
import { fetchRiqueFaq } from '@/lib/riqueApi';
import { exportConversationPdf } from '@/lib/riqueExport';
import { toast } from 'react-hot-toast';
import { useRiqueChatStore } from '@/stores/riqueChatStore';

export interface RiqueModuleLayoutProps {
  companies: Array<{ id: string; name: string }> | undefined;
  companyId: string;
  setCompanyId: (id: string) => void;
  conversations: RiqueConversation[];
  activeId: string | null;
  setActiveId: (id: string) => void;
  activeConversation: RiqueConversation | undefined;
  messages: RiqueMessage[];
  streaming: string;
  loadingReply: boolean;
  progressLabel?: string | null;
  unavailable: boolean;
  llmUnavailable?: boolean;
  statusLoading?: boolean;
  statusUnknown?: boolean;
  onRetryStatus?: () => void;
  alerts: RiqueAlert[] | undefined;
  briefing?: RiqueBriefing | null;
  routeSuggestions: string[];
  lastAssistantText?: string;
  onSend: (text: string) => Promise<void>;
  onStop?: () => void;
  onSendImage?: (file: File) => Promise<void>;
  onNewConversation: () => Promise<unknown>;
  onDeleteConversation?: (conversationId: string) => Promise<void>;
  onDeleteAllConversations?: () => Promise<void>;
  onEmailClick?: () => void;
  userName?: string;
  companyName?: string;
}

export const RiqueModuleLayout: React.FC<RiqueModuleLayoutProps> = ({
  companies,
  companyId,
  setCompanyId,
  conversations,
  activeId,
  setActiveId,
  activeConversation,
  messages,
  streaming,
  loadingReply,
  progressLabel,
  unavailable,
  llmUnavailable = false,
  statusLoading = false,
  statusUnknown = false,
  onRetryStatus,
  alerts,
  briefing,
  routeSuggestions,
  lastAssistantText,
  onSend,
  onStop,
  onSendImage,
  onNewConversation,
  onDeleteConversation,
  onDeleteAllConversations,
  onEmailClick,
  userName,
  companyName,
}) => {
  const routePath = useRiqueChatStore((s) => s.routePath);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileSidebar, setMobileSidebar] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [alertsOpen, setAlertsOpen] = React.useState(false);
  const [faqOpen, setFaqOpen] = React.useState(false);
  const [faqQuery, setFaqQuery] = React.useState('');
  const [faqItems, setFaqItems] = React.useState<RiqueFaqItem[]>([]);
  const [faqTotal, setFaqTotal] = React.useState(80);
  const [faqLoading, setFaqLoading] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const isEmpty = messages.length === 0 && !streaming && !loadingReply;
  const alertCount = alerts?.length ?? 0;

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const loadFaq = React.useCallback(async () => {
    setFaqLoading(true);
    try {
      const data = await fetchRiqueFaq(routePath || undefined);
      setFaqItems(data.items);
      setFaqTotal(data.total);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao carregar FAQ');
    } finally {
      setFaqLoading(false);
    }
  }, [routePath]);

  const openFaq = () => {
    setFaqOpen(true);
    setFaqQuery('');
    setMobileSidebar(false);
    void loadFaq();
  };

  const handleExportPdf = () => {
    setMenuOpen(false);
    if (messages.length === 0) {
      toast.error('Não há mensagens para exportar.');
      return;
    }
    try {
      exportConversationPdf({
        title: activeConversation?.title ?? 'Conversa Rique.Ia',
        messages,
        authorName: userName,
        companyName: companyName ?? activeConversation?.company?.name ?? undefined,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao exportar PDF.');
    }
  };

  const filteredFaq = React.useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return faqItems;
    return faqItems.filter(
      (i) =>
        i.question.toLowerCase().includes(q) ||
        i.domainLabel.toLowerCase().includes(q) ||
        i.intentId.toLowerCase().includes(q)
    );
  }, [faqItems, faqQuery]);

  const faqByDomain = React.useMemo(() => {
    const map = new Map<string, RiqueFaqItem[]>();
    for (const item of filteredFaq) {
      const list = map.get(item.domainLabel) ?? [];
      list.push(item);
      map.set(item.domainLabel, list);
    }
    return map;
  }, [filteredFaq]);

  const canExport = messages.length > 0 && Boolean(activeId);
  const alertPreview = (alerts ?? []).slice(0, 4);

  const sidebar = (
    <RiqueConversationSidebar
      conversations={conversations}
      activeId={activeId}
      onSelect={(id) => {
        setActiveId(id);
        setMobileSidebar(false);
      }}
      onNew={() => {
        void onNewConversation();
        setMobileSidebar(false);
      }}
      onDelete={onDeleteConversation}
      onDeleteAll={onDeleteAllConversations}
      onOpenFaq={openFaq}
      faqTotal={faqTotal}
      className="h-full"
    />
  );

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden bg-background" role="main" aria-label="Rique.Ia">
      {/* Desktop sidebar */}
      <div
        className={cn(
          'hidden shrink-0 transition-[width] duration-200 md:block',
          sidebarOpen ? 'w-[272px]' : 'w-0 overflow-hidden'
        )}
      >
        {sidebarOpen ? sidebar : null}
      </div>

      {/* Mobile drawer */}
      {mobileSidebar ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-foreground/40"
            aria-label="Fechar menu"
            onClick={() => setMobileSidebar(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[min(20rem,88vw)] shadow-2xl">{sidebar}</div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-2 sm:px-4">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 md:hidden"
            onClick={() => setMobileSidebar(true)}
            aria-label="Abrir conversas"
          >
            <Menu className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="hidden h-9 w-9 md:inline-flex"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label={sidebarOpen ? 'Ocultar conversas' : 'Mostrar conversas'}
            title={sidebarOpen ? 'Ocultar conversas' : 'Mostrar conversas'}
          >
            {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
          </Button>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {isEmpty ? 'Nova conversa' : activeConversation?.title?.trim() || 'Conversa'}
            </p>
          </div>

          {alertCount > 0 ? (
            <button
              type="button"
              onClick={() => setAlertsOpen((v) => !v)}
              className={cn(
                'hidden max-w-[10rem] truncate rounded-full border px-2.5 py-1 text-[11px] sm:inline-flex',
                alertsOpen
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-100'
                  : 'border-border/70 bg-muted/40 text-muted-foreground hover:text-foreground'
              )}
              title="Alertas do escopo"
            >
              {alertCount} alerta{alertCount > 1 ? 's' : ''}
            </button>
          ) : null}

          <select
            className="h-9 max-w-[11rem] rounded-xl border border-input bg-background px-2 text-xs text-foreground sm:max-w-[14rem]"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            aria-label="Empresa em contexto"
          >
            <option value="">Todas as empresas</option>
            {(companies ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <div className="relative" ref={menuRef}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Mais opções"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {menuOpen ? (
              <div className="absolute right-0 top-10 z-20 min-w-[12rem] rounded-xl border border-border bg-popover py-1 shadow-lg">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted disabled:opacity-40"
                  disabled={!canExport}
                  onClick={handleExportPdf}
                >
                  <FileDown className="h-3.5 w-3.5" /> Exportar PDF
                </button>
                {onEmailClick ? (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted disabled:opacity-40"
                    disabled={!canExport}
                    onClick={() => {
                      setMenuOpen(false);
                      onEmailClick();
                    }}
                  >
                    <Mail className="h-3.5 w-3.5" /> Enviar e-mail
                  </button>
                ) : null}
                {companyName ? (
                  <p className="border-t border-border/60 px-3 py-2 text-[10px] text-muted-foreground">
                    Escopo: {companyName}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </header>

        {alertsOpen && alertPreview.length > 0 ? (
          <div className="shrink-0 border-b border-border/50 bg-amber-500/5 px-4 py-2">
            <ul className="mx-auto max-w-5xl space-y-1">
              {alertPreview.map((a) => (
                <li key={a.id} className="text-xs text-foreground">
                  <span className="font-medium">{a.titulo}</span>
                  {a.mensagem ? <span className="text-muted-foreground"> — {a.mensagem}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {statusLoading ? (
          <p className="shrink-0 px-4 py-1.5 text-[11px] text-muted-foreground">Verificando status…</p>
        ) : unavailable ? (
          <p className="shrink-0 bg-amber-500/10 px-4 py-1.5 text-[11px] text-amber-900 dark:text-amber-100">
            Módulo Rique.Ia desabilitado no servidor (`RIQUE_ENABLED=false`).
          </p>
        ) : statusUnknown ? (
          <p className="shrink-0 bg-amber-500/10 px-4 py-1.5 text-[11px] text-amber-900 dark:text-amber-100">
            Não foi possível verificar o status do Rique.Ia.
            {onRetryStatus ? (
              <button type="button" className="ml-1 underline underline-offset-2" onClick={() => onRetryStatus()}>
                Tentar de novo
              </button>
            ) : null}
          </p>
        ) : llmUnavailable ? (
          <p className="shrink-0 bg-amber-500/10 px-4 py-1.5 text-[11px] text-amber-900 dark:text-amber-100">
            Perguntas livres exigem OPENAI_API_KEY; chips e FAQ rápida seguem disponíveis.
          </p>
        ) : null}

        <div className="relative flex min-h-0 flex-1 flex-col">
          {faqOpen ? (
            <div className="absolute inset-0 z-20 flex flex-col bg-background">
              <div className="mx-auto flex w-full max-w-5xl items-center gap-2 border-b border-border/80 px-4 py-3">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  value={faqQuery}
                  onChange={(e) => setFaqQuery(e.target.value)}
                  placeholder="Buscar perguntas…"
                  className="h-10 flex-1 rounded-xl border border-input bg-background px-3 text-sm"
                  autoFocus
                />
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" onClick={() => setFaqOpen(false)} aria-label="Fechar FAQ">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="mx-auto w-full max-w-5xl px-4 py-4">
                  {faqLoading ? (
                    <p className="text-center text-sm text-muted-foreground">Carregando…</p>
                  ) : filteredFaq.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground">Nenhuma pergunta encontrada.</p>
                  ) : (
                    [...faqByDomain.entries()].map(([domain, items]) => (
                      <div key={domain} className="mb-5">
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {domain}
                        </p>
                        <ul className="space-y-1">
                          {items.map((item) => (
                            <li key={item.id}>
                              <button
                                type="button"
                                className="w-full rounded-xl px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-muted/70"
                                onClick={() => {
                                  setFaqOpen(false);
                                  void onSend(item.question);
                                }}
                              >
                                {item.question}
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col">
            <RiqueMessageList
              messages={messages}
              streamingContent={streaming}
              isLoading={loadingReply}
              progressLabel={progressLabel}
              onStop={onStop}
              expanded
              briefing={isEmpty ? briefing : undefined}
              emptyMinimal
              suggestions={isEmpty ? routeSuggestions : undefined}
              onSuggestion={isEmpty ? (text) => void onSend(text) : undefined}
            />
            <div className="shrink-0 px-3 pb-3 pt-1 sm:px-4 sm:pb-5">
              <RiqueComposer
                disabled={unavailable}
                busy={loadingReply}
                onSend={onSend}
                onStop={onStop}
                onPickImage={onSendImage}
                lastAssistantText={lastAssistantText}
                suggestions={routeSuggestions}
                onOpenFaq={openFaq}
                faqTotal={faqTotal}
                emptyState={isEmpty}
                autoFocus
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
